import { PageContainer } from '@src/shared/components/page-container';
import { PageTitle } from '@src/shared/components/page-title';
import { PaginationControl } from '@src/design-system/molecules/pagination-control';
import {
  StateBoundary,
  StateBoundaryContent,
  StateBoundaryError,
  StateBoundaryLoading,
  StateBoundaryNotFound,
} from '@src/design-system/state-boundary';
import { Spinner } from '@src/design-system/spinner';
import { Text } from '@src/design-system/text';
import { ReminderHeaderBar } from '@src/features/reminders/components/reminder-header-bar.tsx';
import { ReminderTemplatesCardsView } from '@src/features/reminders/components/reminder-templates-cards-view';
import { ReminderTemplatesTable } from '@src/features/reminders/components/reminder-templates-table/reminder-templates-table';
import { useReminderTemplatesStore } from '@src/features/reminders/store/reminder-templates-store';
import { useSearchParams } from 'react-router-dom';
import reminderTemplateApi from '../services/reminder-template-api';

const getReminderParams = (sp: URLSearchParams) => {
  return {
    name: sp.get('name') || undefined,
    channel: sp.get('channel') || undefined,
    created_by: Number(sp.get('created_by')) || undefined,
    content_type: sp.get('content_type') || undefined,
    page_number: sp.get('page_number')
      ? Number(sp.get('page_number'))
      : undefined,
  };
};

export const RemindersTemplatesPage = () => {
  const [sp, setSp] = useSearchParams();
  const { isTableMode } = useReminderTemplatesStore();
  const reminderParams = getReminderParams(sp);

  const {
    data: reminderTemplates,
    error,
    isPending,
  } = reminderTemplateApi.useFetch(reminderParams, {
    enabled: !isTableMode,
  });

  return (
    <PageContainer>
      <PageTitle titles={['Reminders', 'Templates']} />
      <div className='bg-white border border-primary-200 rounded-2xl pb-4 mb-8 space-y-4'>
        {!isTableMode && <ReminderHeaderBar />}
        {isTableMode ? (
          <div className='p-4 [&_th]:text-gray-800 [&_th]:h-12 [&_td]:text-gray-800 [&_td]:h-12 [&_td]:font-medium [&_th]:font-bold [&_th]:text-sm'>
            <ReminderTemplatesTable />
          </div>
        ) : (
          <StateBoundary
            isLoading={isPending}
            isError={!!error}
            isNotFound={!reminderTemplates?.results?.length}
          >
            <StateBoundaryLoading className='rounded-lg h-full flex items-center justify-center'>
              <Spinner className='size-8 my-50' />
            </StateBoundaryLoading>

            <StateBoundaryError className='rounded-lg flex items-center justify-center'>
              {error?.message}
            </StateBoundaryError>

            <StateBoundaryNotFound className='rounded-lg flex items-center justify-center'>
              <Text className='text-gray-900'>No templates found</Text>
            </StateBoundaryNotFound>

            <StateBoundaryContent className='flex flex-col bg-white p-4'>
              <ReminderTemplatesCardsView
                templates={reminderTemplates?.results ?? []}
              />
              <PaginationControl
                className='mt-4'
                totalPages={reminderTemplates?.total_pages || 0}
                currentPage={reminderParams.page_number || 1}
                onPageChange={(page) =>
                  setSp((prev) => {
                    if (page > 1) {
                      prev.set('page_number', String(page));
                      return prev;
                    }
                    prev.delete('page_number');
                    return prev;
                  })
                }
              />
            </StateBoundaryContent>
          </StateBoundary>
        )}
      </div>
    </PageContainer>
  );
};
